/*
 * Wishlist — save products from anywhere on the storefront and manage them in one drawer.
 *
 * Ported from the R - SW - Wishlist V1 experiment (FigPii 360629), which won. The experiment was
 * Preact mounted at runtime into a theme it did not own; this is the same behaviour as theme code,
 * so the markup is Liquid (snippets/wishlist-star.liquid, snippets/wishlist-drawer.liquid) and this
 * file only gives it behaviour. Everything the experiment had to discover by reading the page —
 * which product a card is for, whether the shopper is signed in, where login goes — is rendered
 * into the markup now and simply read.
 *
 * FOUR CUSTOM ELEMENTS, following the theme's own habit (<cart-drawer>, <vlb-gallery>):
 *
 *   <wishlist-star>     the save control: on a card, on a product page, and in the nav
 *   <wishlist-drawer>   the panel, its list screen and its size step
 *   <wishlist-gallery>  the size step's image viewer — the one piece with state of its own
 *   <wishlist-toast>    the confirmation banner
 *
 * Each subscribes to the store when it connects and unsubscribes when it leaves, which is what
 * makes the whole thing survive the theme replacing its own markup: filtering a collection, sorting
 * it, scrolling another page in, or fetching "You may also like" all return server-rendered cards
 * with their stars already inside them, and each new star wires itself up on connect. The
 * experiment needed a document-wide MutationObserver and a debounced rescan to do that; here there
 * is nothing to observe and nothing to clean up on a removed card's behalf.
 *
 * LAYOUT OF THIS FILE
 *   1. Config        what Liquid told us
 *   2. Storage       localStorage, and the guest retention window
 *   3. Store         one state object, one subscriber list
 *   4. Shopify       product data, money, variants, the cart
 *   5. Elements      the four above
 *
 * EVENTS. The experiment reported eight FigPii goals. Those belonged to the experiment and are
 * gone; in their place every one of the same moments dispatches a `vlb:wishlist` CustomEvent on
 * document, carrying { action, productId, handle, count }. Nothing in the theme listens today — it
 * is there so analytics can be wired to these moments without touching this file.
 */

(function () {
  'use strict';

  /* ── 1. Config ─────────────────────────────────────────────────────────────────────────── */

  const config = (() => {
    const node = document.getElementById('vlb-wishlist-config');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[wishlist] configuration could not be read; the wishlist is inactive', error);
      return null;
    }
  })();

  if (!config) return;

  const strings = config.strings;

  /** Fills the [[name]] slots Liquid left in a string — see the drawer snippet. */
  function fill(template, values) {
    return Object.keys(values).reduce(
      (text, key) => text.split(`[[${key}]]`).join(values[key]),
      template || ''
    );
  }

  /** A storefront URL inside the shopper's own market, so a non-primary locale is never dropped. */
  function shopUrl(path) {
    return `${String(config.routes.root || '/').replace(/\/$/, '')}/${path}`;
  }

  /* ── 2. Storage ────────────────────────────────────────────────────────────────────────── */

  /*
   * Everything here is defensive by design: localStorage is page-owned territory that can be
   * absent, blocked (Safari private browsing, "block all cookies", embedded webviews), full, or
   * hold a value some other script wrote. None of those may break the storefront, so every failure
   * path degrades to "the shopper has an empty wishlist this session" rather than throwing.
   *
   * THIS IS THE SEAM. The list is reached only through readWishlist / writeWishlist / clearWishlist
   * below. A wishlist that follows a signed-in shopper between devices is those three talking to a
   * backend instead of to localStorage — see docs/wishlist.md.
   */

  const STORAGE_KEY = 'vlb:wishlist';
  /** Envelope version. A value written by a foreign shape is discarded. Version 1 is read as-is. */
  const SCHEMA_VERSION = 2;

  /**
   * Versions this build can read. Version 2 allows one product to be saved in more than one size,
   * which 1 did not — but the stored shape never changed, so a version 1 list is already a valid
   * version 2 list and is taken as it stands. Nobody opens the drawer to find their saves gone.
   */
  const READABLE_VERSIONS = [1, 2];

  /**
   * A saved entry's identity.
   *
   * The product alone stopped being enough the moment a shopper could save two sizes of it: the
   * list, the rows, the stars and the size picker all have to tell those two apart, and every one
   * of them used to key on the product id. A size-less save is its own entry rather than a missing
   * one, so it needs a key too.
   */
  function keyOf(productId, variantId) {
    return `${productId}:${variantId || 0}`;
  }

  function itemKey(item) {
    return keyOf(item.id, item.variantId);
  }
  const DAY_IN_MS = 24 * 60 * 60 * 1000;
  const MINUTE_IN_MS = 60 * 1000;

  /**
   * `?wishlist_age=5` — shortens the retention window to that many minutes, so a tester can watch a
   * saved product expire instead of waiting a month for it.
   *
   * Written for QA on a phone, where there is no console to reach into storage with: everything is
   * done from the URL bar. Save a product, open any page with the parameter, wait out the minutes,
   * and load a page with the parameter again — the list is empty and the star is unfilled. Nothing
   * about the expiry is special-cased for the test; only the length of the window changes, and the
   * very same comparison does the removing either way. Fractions are accepted, so `=0.5` is thirty
   * seconds.
   *
   * Two properties keep it safe on a live storefront, in case a real shopper ever lands on such a
   * URL. It can only ever SHORTEN retention — anything longer than the merchant's own setting is
   * refused rather than clamped — and it persists nothing: drop the parameter and the very next
   * read is a plain read again. The one lasting effect is the pruning itself, which is the same
   * removal the drawer's own remove button performs.
   *
   * The one thing to know when using it: pruning happens ON READ, so the parameter has to be on the
   * page load where the removal is expected, not only on the one where the clock started.
   */
  function retentionWindowMs() {
    const days = Number(config.retentionDays) || 30;
    const full = days * DAY_IN_MS;
    const raw = new URLSearchParams(location.search).get('wishlist_age');
    if (raw === null) return full;

    const minutes = Number(raw);
    if (!isFinite(minutes) || minutes <= 0 || minutes * MINUTE_IN_MS > full) return full;

    return minutes * MINUTE_IN_MS;
  }

  /** Narrows an unknown parsed value to a usable entry — anything malformed is dropped silently. */
  function isValidItem(value) {
    return (
      !!value &&
      typeof value === 'object' &&
      typeof value.id === 'number' &&
      isFinite(value.id) &&
      typeof value.handle === 'string' &&
      value.handle.length > 0 &&
      typeof value.addedAt === 'number' &&
      isFinite(value.addedAt)
    );
  }

  /**
   * The shopper's saved wishlist, newest first, with expired entries already pruned.
   *
   * Pruning happens on read rather than on a timer so it costs nothing until the list is actually
   * needed, and the pruned result is written back only when something was removed — a read of a
   * healthy list performs no write at all.
   *
   * The window is never consulted for a signed-in shopper: their list is kept indefinitely, which
   * is exactly what the drawer's "Don't lose your wishlist" panel is promising a guest.
   */
  function readWishlist() {
    let raw;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch (error) {
      return [];
    }
    if (!raw) return [];

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (error) {
      // Not our JSON, or truncated by a full disk. Drop it rather than fight it.
      clearWishlist();
      return [];
    }

    if (!parsed || READABLE_VERSIONS.indexOf(parsed.v) === -1 || !Array.isArray(parsed.items)) {
      clearWishlist();
      return [];
    }

    const valid = parsed.items.filter(isValidItem);
    let kept = valid;

    if (!config.signedIn) {
      const now = Date.now();
      const windowMs = retentionWindowMs();
      kept = valid.filter((item) => now - item.addedAt <= windowMs);
    }

    // Written back only when this read actually changed the list, so the common path stays read-only.
    if (kept.length !== parsed.items.length) writeWishlist(kept);

    return kept.slice().sort((a, b) => b.addedAt - a.addedAt);
  }

  /**
   * Persists the list. A failed write still updates the UI: the shopper gets the wishlist they
   * asked for this session even where storage is blocked, it simply won't outlive the page.
   */
  function writeWishlist(items) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: SCHEMA_VERSION, items }));
      return true;
    } catch (error) {
      return false;
    }
  }

  function clearWishlist() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* storage is unavailable for writing; nothing to undo */
    }
  }

  /* ── 3. Store ──────────────────────────────────────────────────────────────────────────── */

  /*
   * Shared state for every piece of wishlist UI. The nav star, the stars on each product card, the
   * drawer and the confirmation banner all read the same object, which is what keeps them in sync:
   * saving from a product card lights up that card's star, bumps the nav star and adds a row to an
   * already-open drawer without any of them knowing about each other.
   */

  const state = {
    items: readWishlist(),
    isOpen: false,
    toast: null,
    quickViewFor: null,
    sizeChoices: {},
  };

  const subscribers = [];

  /** Returns its own undo, which is what every element's disconnectedCallback calls. */
  function subscribe(fn) {
    subscribers.push(fn);
    return () => {
      const index = subscribers.indexOf(fn);
      if (index !== -1) subscribers.splice(index, 1);
    };
  }

  function notify() {
    subscribers.slice().forEach((fn) => fn(state));
  }

  /** Announces a moment worth measuring. See the note at the top about the experiment's goals. */
  function report(action, item) {
    document.dispatchEvent(
      new CustomEvent('vlb:wishlist', {
        detail: {
          action,
          productId: item ? item.id : null,
          handle: item ? item.handle : null,
          count: state.items.length,
        },
      })
    );
  }

  /** Writes the list as part of the same step that changes it, so the two never disagree. */
  function persist(items) {
    writeWishlist(items);
    return items;
  }

  /**
   * Everything that follows a product leaving the list, whichever of the two ways it left: it goes
   * from storage, the size step stops sizing it, and the size picked for it is forgotten.
   *
   * The size goes because it belonged to a product that is no longer in the list — saving it again
   * would otherwise open onto a choice made before it was removed, and adding it to the bag again
   * would open onto the size already in the bag.
   *
   * What it deliberately does NOT decide is the banner: un-saving takes one down and bagging puts
   * one up, so each caller says that for itself.
   */
  function withoutItem(key) {
    state.items = persist(state.items.filter((item) => itemKey(item) !== key));
    if (state.quickViewFor === key) state.quickViewFor = null;
    delete state.sizeChoices[key];
  }

  const actions = {
    /** Re-reads storage. Used when another tab changes the list. */
    hydrate() {
      state.items = readWishlist();
      notify();
    },

    /** Saves a product. Re-adding one already saved is a no-op, so its addedAt never resets. */
    add(payload) {
      /*
       * Two sizes of one product are two entries, so the thing that must not be added twice is the
       * SIZE, not the product. Saving a size already saved is the only no-op left.
       */
      const key = keyOf(payload.id, payload.variantId);
      if (state.items.some((item) => itemKey(item) === key)) return;

      const item = { id: payload.id, handle: payload.handle, addedAt: Date.now() };
      if (payload.variantId) item.variantId = payload.variantId;

      state.items = persist([item, ...state.items]);
      state.toast = { handle: payload.handle, kind: 'saved' };
      report('added', item);
      notify();
    },

    /**
     * Removes a product because the shopper asked for it gone.
     *
     * A removal also takes down that product's confirmation banner. Un-saving inside the seconds it
     * is up otherwise leaves "Added to Your Wishlist!" on screen for something that is no longer
     * saved — the shopper's last action contradicted by the message still sitting above it.
     */
    remove(key) {
      const removed = state.items.find((item) => itemKey(item) === key);
      if (!removed) return;

      withoutItem(key);
      if (state.toast && state.toast.handle === removed.handle) state.toast = null;
      report('removed', removed);
      notify();
    },

    /**
     * Moves a product out of the wishlist because it has just gone into the bag, and says so.
     *
     * Its own action rather than `remove` plus a banner, and that separation is the point. `remove`
     * is the shopper saying they no longer want the product; routing an automatic removal through
     * it would report a decision the code made as one they made. The banner is not decoration on
     * top of that — it is what explains it: a row vanishing on its own reads as the wishlist losing
     * something, and on a page with the theme's cart drawer the wishlist has already closed behind
     * it, so the banner is the only place the disappearance is accounted for.
     *
     * Called only after the add has succeeded — a failed add leaves the wishlist exactly as it was,
     * so nothing is ever lost to a cart that refused it.
     */
    bag(key) {
      const bagged = state.items.find((item) => itemKey(item) === key);
      if (!bagged) return;

      withoutItem(key);
      state.toast = { handle: bagged.handle, kind: 'bagged' };
      report('bagged', bagged);
      notify();
    },

    /**
     * Opens or closes the drawer. Closing always returns it to the list, never the size step, and
     * drops the sizes picked in it — they belong to the visit the shopper just ended.
     */
    setOpen(isOpen) {
      if (state.isOpen === isOpen) return;
      state.isOpen = isOpen;
      if (isOpen) {
        report('opened', null);
      } else {
        state.quickViewFor = null;
        state.sizeChoices = {};
      }
      notify();
    },

    /** Opens the size step for a product, or returns to the list with null. */
    setQuickView(key) {
      if (state.quickViewFor === key) return;
      state.quickViewFor = key;
      notify();
    },

    /**
     * Records the size chosen for one product. Only ever a HINT to the size step, never an
     * instruction: it is re-checked against the live variant list before it is shown, so a size
     * that sells out while the drawer is open stops being offered rather than sitting there ready
     * to add.
     */
    setSizeChoice(key, variantId) {
      state.sizeChoices[key] = variantId;
      notify();
    },

    hideToast() {
      if (state.toast === null) return;
      state.toast = null;
      notify();
    },
  };

  /**
   * Whether THIS entry is saved — a product in a particular size, or a product with no size.
   *
   * There is one question now rather than two. Every star stands for exactly the entry pressing it
   * would create or remove, so what it shows and what it does can never disagree.
   */
  function isSavedKey(key) {
    return state.items.some((item) => itemKey(item) === key);
  }

  /*
   * A shopper with the store open twice would otherwise have two wishlists that overwrite each
   * other: localStorage is shared, but each tab read it once at load and never hears about the
   * other's writes, so the second tab to save would persist its own stale list on top. The
   * browser's `storage` event fires in every OTHER tab on a write, and re-reading on it keeps them
   * in step. A null key means another tab called localStorage.clear(), which took ours with it.
   */
  window.addEventListener('storage', (event) => {
    if (event.key !== null && event.key !== STORAGE_KEY) return;
    actions.hydrate();
  });

  /* ── 4. Shopify ────────────────────────────────────────────────────────────────────────── */

  /*
   * Nothing renderable about a product is persisted — title, image, size list, availability and
   * price are read from Shopify each time the drawer opens. That is what keeps the number a shopper
   * sees in the wishlist the number the cart will charge, even if the merchant repriced the
   * product, sold out a size or renamed it after the item was saved.
   */

  const productCache = {};

  /** The handle of the product page the shopper is on, or null anywhere else. */
  function currentProductHandle() {
    const match = location.pathname.split('/products/')[1];
    return match ? decodeURIComponent(match.split('/')[0].split('?')[0]) : null;
  }

  /**
   * One product's live data.
   *
   * Cached, because the drawer re-reads the same handles every time it opens — with one exception.
   * The product whose page the shopper is standing on is re-fetched every time: that is the one row
   * whose data they can check against the page behind the drawer, and a cached price or a "Sold
   * out" that disagrees with the product page they are looking at is the version of stale that gets
   * noticed. It is also the cheapest possible exception — one product, on a page they had to load
   * anyway.
   */
  function fetchProduct(handle) {
    if (handle === currentProductHandle()) delete productCache[handle];
    if (productCache[handle]) return productCache[handle];

    productCache[handle] = fetch(shopUrl(`products/${encodeURIComponent(handle)}.js`), {
      headers: { Accept: 'application/json' },
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((product) => {
        // Shape check: the endpoint is the storefront's, so nothing is assumed.
        if (!product || typeof product.id !== 'number' || !Array.isArray(product.variants)) return null;
        return product;
      })
      .catch(() => {
        // The product may have been unpublished since it was saved, or the connection may have
        // failed outright. Either way the item is dropped from the RENDERED list; the saved entry
        // itself is left alone, so a transient failure never silently erases the wishlist.
        delete productCache[handle];
        return null;
      });

    return productCache[handle];
  }

  /**
   * Resolves every saved item against Shopify, newest first, dropping the ones that no longer
   * exist. The requests run in parallel and each failure is contained to its own entry, so one
   * deleted product cannot blank the whole drawer. Order follows the saved list, not response
   * timing.
   */
  function loadEntries(items) {
    return Promise.all(
      items.map((item) => fetchProduct(item.handle).then((product) => (product ? { item, product } : null)))
    ).then((entries) => entries.filter(Boolean));
  }

  /**
   * The option the drawer's quick view lets a shopper choose.
   *
   * Matched by name, not position: Valabasas products carry Color at position 1 and Size at
   * position 2, but that ordering is a merchandising choice the client can change at any time.
   */
  const SIZE_OPTION = /size/i;

  /** The .js endpoint returns options as plain strings on some shops and objects on others. */
  function optionName(option) {
    return typeof option === 'string' ? option : (option && option.name) || '';
  }

  /**
   * The size one variant IS, for printing on a row — as opposed to the sizes a product offers.
   *
   * Reads the same option this file uses everywhere else, so a shop that renames or reorders its
   * options moves both together. A product with no size option, or a variant that has lost its
   * options, returns nothing and the row simply shows no size rather than an empty label.
   */
  function sizeLabelOf(product, variant) {
    if (!variant) return '';
    const options = product.options || [];
    const index = options.findIndex((option) => SIZE_OPTION.test(optionName(option)));
    if (index === -1) return '';
    return (variant.options && variant.options[index]) || '';
  }

  /**
   * The sizes this product offers, in the order the merchant put them.
   *
   * The saved variant pins the OTHER options (colour), so the sizes offered are the sizes of the
   * colourway the shopper actually saved rather than of whichever variant happened to come first.
   */
  function getSizeChoices(product, pinnedVariantId) {
    const options = product.options || [];
    const index = options.findIndex((option) => SIZE_OPTION.test(optionName(option)));
    if (index === -1) return [];

    const pinned = product.variants.find((variant) => variant.id === pinnedVariantId);
    const seen = new Set();
    const choices = [];

    product.variants.forEach((variant) => {
      const label = variant.options ? variant.options[index] : null;
      if (label == null || seen.has(label)) return;

      if (pinned) {
        const sameColourway = variant.options.every(
          (value, position) => position === index || value === pinned.options[position]
        );
        if (!sameColourway) return;
      }

      seen.add(label);
      choices.push({ variantId: variant.id, label, available: variant.available === true });
    });

    return choices;
  }

  /**
   * Whether a saved product has nothing left on it a shopper could actually buy.
   *
   * Judged from the sizes the quick view WOULD OFFER rather than from the product's own `available`
   * flag, because the two are not the same question. The flag is true while any variant anywhere on
   * the product is in stock — including a colour the shopper did not save — so a row can read as
   * buyable and then open onto a picker where every size is greyed out. Asking the same call the
   * picker asks keeps the row and the step behind it telling one story.
   *
   * A product with no size option at all — the catalogue has a few — has no picker to consult, so
   * there the product's flag is the only answer there is.
   */
  function isSoldOut(entry) {
    const choices = getSizeChoices(entry.product, entry.item.variantId);
    if (choices.length === 0) return entry.product.available === false;
    return choices.every((choice) => !choice.available);
  }

  /* Money ------------------------------------------------------------------------------------ */

  /** An ISO code sitting in the format string, e.g. the USD of "${{amount}} USD". */
  const ISO_CODE = /\b[A-Z]{3}\b/;

  /**
   * The storefront's money format, or null when it cannot be trusted for the active currency.
   *
   * The trust check is the point. The format is a Liquid-rendered snapshot: correct for the market
   * the page was served for, and stale the moment anything switches currency without a reload.
   * Since the format usually NAMES its currency, a code in the string that disagrees with
   * Shopify.currency.active is proof it is stale — and a wrong currency is far worse than a
   * differently-punctuated right one, so that case falls back rather than guesses.
   */
  function trustedMoneyFormat() {
    const format = config.moneyFormat;
    if (typeof format !== 'string' || !format) return null;

    const declared = (format.match(ISO_CODE) || [])[0];
    const active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;

    return declared && active && declared !== active ? null : format;
  }

  function group(number, separator) {
    return number.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${separator}`);
  }

  /** Shopify's own money-format placeholders, rendered the way Liquid's `money` filter does. */
  function applyMoneyFormat(cents, format) {
    const amount = (Number(cents) || 0) / 100;

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, placeholder) => {
      switch (placeholder) {
        case 'amount':
          return group(amount.toFixed(2), ',');
        case 'amount_no_decimals':
          return group(Math.round(amount).toFixed(0), ',');
        case 'amount_with_comma_separator':
          return group(amount.toFixed(2), '.').replace(/\.(\d\d)$/, ',$1');
        case 'amount_no_decimals_with_comma_separator':
          return group(Math.round(amount).toFixed(0), '.');
        case 'amount_with_apostrophe_separator':
          return group(amount.toFixed(2), "'");
        case 'amount_with_space_separator':
          return group(amount.toFixed(2), ' ').replace(/\.(\d\d)$/, ',$1');
        case 'amount_no_decimals_with_space_separator':
          return group(Math.round(amount).toFixed(0), ' ');
        case 'amount_with_period_and_space_separator':
          return group(amount.toFixed(2), ' ');
        default:
          return '';
      }
    });
  }

  /**
   * A price written exactly as the rest of the storefront writes it — "$105.00 USD", "LE 8,807.00
   * EGP" — by reusing the shop's own format string, and falling back to Intl where it is missing.
   *
   * The fallback is not equivalent, which is why it is only a fallback: Intl renders Egyptian
   * pounds as "E£8,807.00", a symbol the storefront never uses, and one that wraps to a second line
   * in the drawer's narrow rows where "LE 8,807.00 EGP" does not. narrowSymbol keeps that path from
   * writing "CA$206.00 CAD", since the appended code already disambiguates the currency.
   */
  function formatMoney(cents) {
    const format = trustedMoneyFormat();
    if (format) return applyMoneyFormat(cents, format);

    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return ((Number(cents) || 0) / 100).toFixed(2);
    }
  }

  /**
   * A Shopify CDN image URL asked to render at a given width.
   *
   * Shopify serves any cdn.shopify.com image at an arbitrary width from the `width` query
   * parameter, so the size actually painted is the size downloaded — a 2048px original behind a
   * 30px gallery thumbnail is ~40x the bytes for no visible difference. Pass the RENDERED width,
   * doubled for retina.
   *
   * The URL is parsed rather than string-concatenated because product images already carry a `?v=`
   * cache-buster, and URL handles that (plus protocol-relative //cdn... URLs) correctly. Anything
   * unparsable is handed back untouched: a slightly heavy image is a far better outcome than a
   * broken src.
   */
  function sized(url, width) {
    if (!url) return '';
    try {
      const parsed = new URL(url, location.origin);
      parsed.searchParams.set('width', String(Math.round(width)));
      return parsed.toString();
    } catch (error) {
      return url;
    }
  }

  /**
   * A saved product's page, inside the shopper's market.
   *
   * Built from the handle rather than taken from the product JSON's own `url`, which is
   * root-relative and would drop a shopper browsing /en-ca/ back onto the primary market. The
   * variant is appended when the shopper had a size saved and it still exists, so the page they
   * land on is priced at the number the row was quoting — without it the PDP opens on its default
   * variant, which on a catalogue with size-dependent prices contradicts the row they just clicked.
   */
  function productUrl(handle, variantId) {
    const url = shopUrl(`products/${handle}`);
    return variantId ? `${url}?variant=${variantId}` : url;
  }

  /* Cart ------------------------------------------------------------------------------------- */

  /*
   * Valabasas runs Dawn's <cart-drawer>, so after a successful add the theme re-renders its own
   * drawer and its own count bubble from its own markup. We reimplement none of it.
   *
   * That choice decides the post-add behaviour. Dawn's renderContents() refreshes the sections AND
   * opens the cart drawer; there is no supported way to get the refresh without the reveal. The
   * alternative — writing the returned section HTML into the theme's nodes ourselves — means
   * hand-maintaining bookkeeping the theme already does in three files. So the wishlist hands off:
   * it closes itself and lets the site's own cart drawer take over, which is what adding to bag
   * does everywhere else on this storefront.
   *
   * Where no <cart-drawer> exists the add still succeeds — the drawer stays open and the banner
   * confirms it, and the count bubble catches up on the next navigation.
   */

  /** The ids Dawn's own getSectionsToRender() returns on this storefront. */
  const CART_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  /** The header cart link — the theme's own trigger for the drawer, and where it returns focus to. */
  const HEADER_CART = '#cart-icon-bubble';

  /**
   * Opens the theme's cart drawer, returning whether there was one to open.
   *
   * Focus is handed to the theme with the HEADER CART ICON as the trigger, not the button that was
   * clicked: the wishlist closes behind the cart drawer, so returning focus to a control inside it
   * would land a keyboard shopper on something hidden. The header icon is the same element the
   * theme itself passes, and it is still on screen when the cart closes.
   */
  function openCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return false;

    drawer.open(document.querySelector(HEADER_CART) || undefined);
    return true;
  }

  /**
   * Adds one unit of a variant to the cart and hands off to the theme's cart drawer.
   *
   * Never throws: every outcome the size step has to render — success, Shopify's own refusal (a
   * sold-out size, an inventory limit), a dead connection — comes back as a plain result object.
   *
   * Unlike the experiment, nothing here has to clear the theme's `is-empty` class: this theme's own
   * renderContents() drops it before rendering (assets/cart-drawer.js), so the shopper cannot be
   * left looking at "Your cart is empty" above the item they just added.
   */
  function addToBag(variantId) {
    const drawer = document.querySelector('cart-drawer');
    const payload = new FormData();
    payload.append('id', String(variantId));
    payload.append('quantity', '1');

    if (drawer) {
      // No point paying for the theme's re-render on a page with no cart drawer to put it in.
      payload.append('sections', CART_SECTIONS.join(','));
      payload.append('sections_url', location.pathname);
    }

    const url = (window.routes && window.routes.cart_add_url) || shopUrl('cart/add.js');

    return fetch(url, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: payload,
    })
      .then((response) => response.json())
      .then((result) => {
        // A real line item has a numeric id; a refusal carries `description` instead. Shopify's
        // description is shopper-facing copy ("All 1 of these are in your cart."), so showing it
        // beats replacing it with something vaguer.
        if (!result || typeof result.id !== 'number') {
          return { ok: false, reason: (result && result.description) || strings.genericError };
        }

        let handedOff = false;
        if (drawer && typeof drawer.renderContents === 'function' && result.sections) {
          drawer.renderContents(result);
          handedOff = true;
        }

        return { ok: true, handedOffToCart: handedOff };
      })
      .catch(() => ({ ok: false, reason: strings.genericError }));
  }

  /**
   * Empties one cart line, for a star that has just moved its product to the wishlist.
   *
   * `/cart/change.js` with quantity 0 rather than a remove route, because a line key is what the row
   * carries and change.js is the one endpoint that takes one. The sections ride along and the theme
   * renders them, so the drawer redraws itself exactly as it does after any other cart change —
   * no second request, and no re-render of our own to keep in step with theirs.
   *
   * A failure is swallowed on purpose. The product is already saved by the time this runs, so the
   * worst case is a line that outlives its save and goes on the shopper's next cart action; putting
   * an error in front of them for that would be louder than the thing that went wrong.
   */
  function emptyCartLine(lineKey) {
    const drawer = document.querySelector('cart-drawer');
    const payload = { id: lineKey, quantity: 0 };

    if (drawer) {
      payload.sections = CART_SECTIONS.join(',');
      payload.sections_url = location.pathname;
    }

    return fetch((window.routes && window.routes.cart_change_url) || shopUrl('cart/change.js'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
      .then((response) => (response.ok ? response.json() : null))
      .then((result) => {
        if (drawer && result && result.sections && typeof drawer.renderContents === 'function') {
          drawer.renderContents(result);
        }
      })
      .catch(() => {
        /* see above: the save already happened, and the next cart action redraws the drawer */
      });
  }

  /* ── 5. Elements ───────────────────────────────────────────────────────────────────────── */

  /** Where focus returns when the drawer closes — the nav star that opened it. */
  let lastOpener = null;

  /** Everything Shopify renders into the header group: the strip the page pins over its own top. */
  const HEADER_GROUP = '.shopify-section-group-header-group';

  /** The lowest edge of that strip in the viewport, clamped at the top of the viewport itself. */
  function headerGroupBottom() {
    let lowest = 0;
    document.querySelectorAll(HEADER_GROUP).forEach((section) => {
      lowest = Math.max(lowest, section.getBoundingClientRect().bottom);
    });
    return lowest;
  }

  /* <wishlist-star> -------------------------------------------------------------------------- */

  /**
   * The save control, in all three of its placements: on a product card in a grid or carousel, on
   * the product page's gallery, and in the header.
   *
   * One element rather than three because only two things differ between them: where it sits (a
   * context modifier the stylesheet keys off) and whether it saves a product or opens the drawer.
   * The behaviour that matters — what "saved" means, how it is announced — is written once, so the
   * nav star and a card star can never drift apart.
   *
   * It has no markup of its own: the button is rendered by Liquid and this only gives it behaviour,
   * which is what lets a card arrive from Shopify's section rendering already complete and needing
   * only to be connected.
   */
  class WishlistStar extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      if (!this.button) return;

      this.context = this.getAttribute('context');
      this.isNav = this.context === 'nav';
      this.productId = Number(this.getAttribute('product-id'));
      this.bubble = this.querySelector('.vlb-wl-star__bubble');

      this.onScroll = this.place.bind(this);
      this.button.addEventListener('click', this.toggle.bind(this));

      // Only a product star has a bubble; the nav star is labelled by the icon row around it.
      if (this.bubble) {
        this.button.addEventListener('pointerenter', this.showBubble.bind(this));
        this.button.addEventListener('pointerleave', this.hideBubble.bind(this));
        this.button.addEventListener('focus', this.showBubble.bind(this));
        this.button.addEventListener('blur', this.hideBubble.bind(this));
      }

      this.unsubscribe = subscribe(this.render.bind(this));

      /*
       * A product-page star stands for the size on screen, so changing the size changes what it
       * stands for — and the store has not changed, so nothing would otherwise tell it to re-read.
       * The theme announces the change on the window; this listens for that rather than watching
       * the variant field, which the theme rewrites programmatically without firing anything.
       */
      if (this.context === 'pdp') {
        this.onVariantChange = this.render.bind(this);
        window.addEventListener('variantChange', this.onVariantChange);
      }

      this.render();
    }

    /*
     * The theme replaces its product grid wholesale on a filter or sort change, so a star leaves the
     * page without warning — mid-hover, and possibly while its scroll listener is registered. This
     * is what makes that safe, and it is the reason these are custom elements rather than delegated
     * handlers: nothing has to notice the removal or clean up on its behalf.
     */
    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      window.removeEventListener('scroll', this.onScroll);
      if (this.onVariantChange) window.removeEventListener('variantChange', this.onVariantChange);
    }

    /**
     * The nav star reports the WISHLIST'S state, not a product's: it fills on the first save and
     * stays filled until the list is empty again. It is the only indicator that a save landed once
     * the confirmation banner has gone.
     */
    static navLabel(count) {
      if (count === 0) return strings.navEmpty;
      if (count === 1) return strings.navOne;
      return fill(strings.navOther, { count });
    }

    render() {
      if (this.isNav) {
        const count = state.items.length;
        this.button.classList.toggle('vlb-wl-star--saved', count > 0);
        this.button.setAttribute('aria-expanded', String(state.isOpen));
        this.button.setAttribute('aria-label', WishlistStar.navLabel(count));
        return;
      }

      const saved = this.savedHere();
      const title = this.getAttribute('product-title') || strings.unnamedProduct;

      this.button.classList.toggle('vlb-wl-star--saved', saved);
      this.button.setAttribute('aria-pressed', String(saved));
      this.button.setAttribute(
        'aria-label',
        fill(saved ? strings.starRemove : strings.starAdd, { product: title })
      );
    }

    /**
     * The variant the shopper currently has selected on a product page, if any.
     *
     * Read at click time rather than cached: the theme rewrites this field every time a size is
     * picked, so anything read at load would be stale by the time the star is pressed. The field is
     * disabled while the selected variant is sold out but still carries its value, and a sold-out
     * choice is worth saving — the drawer re-validates every hint against live stock before using
     * it.
     */
    static selectedVariantId() {
      const input = document.querySelector('input[name="id"][data-vlb-variant-input]');
      const id = input ? Number(input.value) : NaN;
      return isFinite(id) && id > 0 ? id : undefined;
    }

    toggle() {
      if (this.isNav) {
        lastOpener = this.button;
        actions.setOpen(!state.isOpen);
        return;
      }

      if (!isFinite(this.productId) || this.productId <= 0) return;

      if (this.savedHere()) {
        actions.remove(this.entryKey());
        return;
      }

      actions.add({
        id: this.productId,
        handle: this.getAttribute('product-handle'),
        // A product page reads its picker; a mini-cart line already knows the size and carries it;
        // a card star has neither and saves the product on its own.
        variantId: this.variantHere(),
      });

      /*
       * Saving from the mini cart MOVES the product rather than copying it, so the line goes as soon
       * as it is saved. The banner announcing it has its own life and is not waited on: it is
       * already on screen, and holding the removal back until it faded would leave the product
       * sitting in both places while the shopper watched.
       *
       * ONLY WHEN THE SAVE ACTUALLY TOOK, which is asked of the store rather than assumed. Saving
       * a size already saved does nothing, and emptying the line regardless would take it out of
       * the cart, put nothing in the wishlist and show no banner to say either — the shopper would
       * simply lose it.
       */
      const lineKey = this.getAttribute('line-key');
      if (this.context === 'minicart' && lineKey && this.savedHere()) emptyCartLine(lineKey);
    }

    /**
     * The size this star stands for, which is a different question in each placement.
     *
     * A product page reads whatever is selected right now — CRO's ruling is that the star follows
     * the size, so a shopper switching from a size they saved to one they have not sees the star
     * empty and can save that one too. A mini-cart line already knows its size and carries it. A
     * card has no size to follow, so it stands for the size-less save, which is its own entry.
     */
    variantHere() {
      if (this.context === 'pdp') return WishlistStar.selectedVariantId();
      const id = Number(this.getAttribute('variant-id'));
      return isFinite(id) && id > 0 ? id : undefined;
    }

    /** The entry this star creates or removes. */
    entryKey() {
      return keyOf(this.productId, this.variantHere());
    }

    /**
     * Whether THIS control should read as saved.
     *
     * Every star answers for its own entry, so what it shows is exactly what pressing it would
     * undo. A star that fills because some other size of the same product is saved would be telling
     * the shopper the size in front of them is in their wishlist when it is not.
     */
    savedHere() {
      return isSavedKey(this.entryKey());
    }

    /**
     * The lowest edge in the viewport that the bubble's top may not cross.
     *
     * Two sources, and the higher one wins. The header group — the whole group and not just the
     * header, because on this storefront the announcement bar is a sibling UNDERNEATH it. And
     * anything on the way up that clips, because a bubble drawn outside an overflow: hidden ancestor
     * is sliced off whether or not the viewport had room for it. Stars are placed to avoid that — a
     * card's host is .card-wrapper, which does not clip — so on this storefront the walk finds
     * nothing and simply confirms it. It stays because it is what makes that placement optional
     * rather than load-bearing: a client who starts clipping something new is answered correctly
     * instead of showing a sliced bubble. Only overflow-y is read: a box that clips on one axis
     * alone computes the other to `auto` rather than leaving it `visible`.
     */
    ceiling() {
      let lowest = headerGroupBottom();

      for (let node = this.parentElement; node && node !== document.body; node = node.parentElement) {
        if (getComputedStyle(node).overflowY !== 'visible') {
          lowest = Math.max(lowest, node.getBoundingClientRect().top);
        }
      }

      return lowest;
    }

    /**
     * Which side the hover bubble hangs on.
     *
     * The design draws it ABOVE, and above is where it belongs: it is the side that reads as a label
     * of the star rather than a caption of the image underneath it. What it cannot be is ALWAYS
     * above. The star sits in the top-right corner of a product image, so the 39px the bubble needs
     * up there is the first thing the page takes away — the strip the site pins over its own top
     * edge sits in it, whenever the star has travelled far enough up the viewport.
     *
     * THE FAILURE DIRECTION IS DELIBERATE: every path that cannot measure leaves the class off, and
     * the stylesheet's default is "below" — so a star not laid out yet, a missing header or a device
     * with no bubble at all lands on the placement that is known to work everywhere.
     *
     * The bubble's height is read off the element rather than repeated from the stylesheet, so the
     * answer stays true if the copy or the type ever changes it — and a bubble with no box at all
     * (display: none on a touch device) can only report "no".
     */
    place() {
      const height = this.bubble.getBoundingClientRect().height;
      const fits =
        height > 0 && this.button.getBoundingClientRect().top - 10 - height >= this.ceiling();

      this.button.classList.toggle('vlb-wl-star--bubble-above', fits);
    }

    /*
     * Measured on pointerenter / focus rather than continuously, because it is only ever needed for
     * the one star a shopper is on — measuring every star on a collection page would be twenty rect
     * reads to answer a question nineteen of them are not asking.
     *
     * FOLLOWED WHILE IT IS UP, because the page can move under a bubble that is already showing.
     * Pointer hover mostly ends itself — the star scrolls out from under a stationary cursor within
     * its own 32px — but keyboard focus does not: Page Down with the star focused scrolls the whole
     * document with the bubble still up, which is precisely the case QA reported, and a keyboard
     * user has no way to nudge it back. The listener is registered only while a bubble is showing,
     * so a page of cards carries no scroll work at rest.
     */
    showBubble() {
      // Off the pointer breakpoint the bubble is display: none, so it has no box — and a touch
      // device gets no scroll listener out of this.
      if (this.bubble.offsetHeight === 0) return;

      this.place();
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    hideBubble() {
      // Except while the button still has focus, where the bubble is still up and still needs
      // following after the pointer has left it.
      if (this.button === document.activeElement) return;
      window.removeEventListener('scroll', this.onScroll);
    }
  }

  /* <wishlist-gallery> ----------------------------------------------------------------------- */

  /**
   * The size step's image viewer: one large image, a vertical rail of thumbnails, prev/next arrows.
   *
   * Its own element because it is the one piece of the drawer with state of its own — which image is
   * showing — that nothing else needs to know about. The drawer hands it a product through show()
   * and it takes care of the rest.
   *
   * The rail renders EVERY image the product has rather than the eight the mock happens to show: the
   * design draws a fixed eight-slot stack because the mocked product has eight photos, and silently
   * dropping the ninth would hide product the shopper is deciding on. Past eight the rail scrolls,
   * so at the designed count it is pixel-identical to the mock.
   *
   * Arrows are genuinely disabled at the ends rather than wrapping: the design draws the prev arrow
   * at half opacity on the first slide, which is a disabled state, and a gallery that jumps from the
   * first photo to the last is disorienting.
   */
  class WishlistGallery extends HTMLElement {
    /** Rendered widths at the design viewports. */
    static STAGE_WIDTH = 342;
    static RAIL_WIDTH = 36;

    connectedCallback() {
      this.rail = this.querySelector('[data-wishlist-rail]');
      this.stage = this.querySelector('[data-wishlist-stage]');
      this.prev = this.querySelector('[data-wishlist-image-prev]');
      this.next = this.querySelector('[data-wishlist-image-next]');
      this.index = 0;
      this.images = [];

      this.prev.addEventListener('click', () => this.step(-1));
      this.next.addEventListener('click', () => this.step(1));
    }

    /** Called by the drawer whenever the size step lands on a product. */
    show(product) {
      const images = (
        product.images && product.images.length ? product.images : [product.featured_image]
      ).filter(Boolean);

      // A different product means a different set of images — start it at the first one rather than
      // holding an index that may not exist in the new set.
      const changed = this.productId !== product.id;
      this.productId = product.id;
      this.title = product.title;
      this.images = images;

      if (changed) {
        this.index = 0;
        this.buildRail();
      }

      this.hidden = images.length === 0;
      this.render();
    }

    buildRail() {
      this.rail.textContent = '';
      if (this.images.length < 2) return;

      this.images.forEach((image, position) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'vlb-wl-gallery__thumb';
        button.setAttribute(
          'aria-label',
          fill(strings.galleryShow, { index: position + 1, count: this.images.length })
        );
        button.addEventListener('click', () => {
          this.index = position;
          this.render();
        });

        const thumb = document.createElement('img');
        thumb.src = sized(image, WishlistGallery.RAIL_WIDTH * 2);
        thumb.alt = '';
        thumb.loading = 'lazy';
        thumb.draggable = false;
        // The button already carries the 36:45 aspect ratio, so these only give the browser the
        // intrinsic ratio before the file lands; the stylesheet sizes what is finally painted.
        thumb.width = WishlistGallery.RAIL_WIDTH;
        thumb.height = 45;

        button.appendChild(thumb);
        item.appendChild(button);
        this.rail.appendChild(item);
      });
    }

    step(delta) {
      this.index = Math.min(Math.max(this.index + delta, 0), this.images.length - 1);
      this.render();
      this.scrollRail();
    }

    /**
     * Keeps the chosen thumbnail inside the rail.
     *
     * The rail's own scrollTop rather than scrollIntoView, which walks EVERY scrollable ancestor:
     * the size step is one of them, so it would drag the whole screen back to the top of the gallery
     * whenever the shopper had scrolled down to the size field.
     */
    scrollRail() {
      const thumb = this.rail.children[this.index];
      if (!thumb) return;

      const listBox = this.rail.getBoundingClientRect();
      const thumbBox = thumb.getBoundingClientRect();

      if (thumbBox.top < listBox.top) this.rail.scrollTop -= listBox.top - thumbBox.top;
      else if (thumbBox.bottom > listBox.bottom) this.rail.scrollTop += thumbBox.bottom - listBox.bottom;
    }

    render() {
      if (!this.images.length) return;

      const index = Math.min(this.index, this.images.length - 1);
      this.index = index;
      const many = this.images.length > 1;

      this.rail.hidden = !many;
      Array.prototype.forEach.call(this.rail.children, (item, position) => {
        const button = item.firstElementChild;
        button.classList.toggle('vlb-wl-gallery__thumb--active', position === index);
        button.setAttribute('aria-current', String(position === index));
      });

      this.stage.src = sized(this.images[index], WishlistGallery.STAGE_WIDTH * 2);
      this.stage.alt = fill(strings.galleryAlt, {
        product: this.title,
        index: index + 1,
        count: this.images.length,
      });

      this.prev.hidden = !many;
      this.next.hidden = !many;
      this.prev.disabled = index === 0;
      this.next.disabled = index === this.images.length - 1;
    }
  }

  /* <wishlist-drawer> ------------------------------------------------------------------------ */

  /** The id a row's "Choose Size" button carries, so focus can find it again after a screen swap. */
  function sizeButtonId(key) {
    return `vlb-wl-row-size-${key}`;
  }

  /** The id a row's remove button carries, for the same reason. */
  function removeButtonId(key) {
    return `vlb-wl-row-remove-${key}`;
  }

  /**
   * A product's price, with its pre-discount price struck through when there is one.
   *
   * The design was drawn from full-price products and shows a single number, but the catalogue runs
   * discounts of 60% and more. Printing only the sale price would hide the saving from the very
   * screen a shopper opens to decide what to buy. Order and treatment follow the storefront's own
   * price snippet; what is NOT copied is its #ff0000 sale colour, which measures 4.0:1 on white and
   * misses the 4.5:1 WCAG 2.2 AA floor for body text — the discount is carried by the strikethrough
   * and the position instead.
   *
   * The two visually-hidden labels are what make it unambiguous without the visual: a screen reader
   * given two bare numbers has no way to tell which one the shopper would pay, and <s> is not
   * announced by most of them.
   */
  function renderPrice(target, price, compareAt) {
    const onSale = typeof compareAt === 'number' && compareAt > price;
    target.textContent = '';

    if (onSale) {
      const regularLabel = document.createElement('span');
      regularLabel.className = 'vlb-wl-visually-hidden';
      regularLabel.textContent = strings.priceRegular;
      target.appendChild(regularLabel);

      const struck = document.createElement('s');
      struck.className = 'vlb-wl-price__compare';
      struck.textContent = formatMoney(compareAt);
      target.appendChild(struck);
    }

    const currentLabel = document.createElement('span');
    currentLabel.className = 'vlb-wl-visually-hidden';
    currentLabel.textContent = onSale ? strings.priceSale : strings.price;
    target.appendChild(currentLabel);

    const current = document.createElement('span');
    current.className = 'vlb-wl-price__current';
    current.textContent = formatMoney(price);
    target.appendChild(current);
  }

  /**
   * The wishlist itself: a right-anchored, full-height panel over the page, with two screens.
   *
   * The list is the default; picking "Choose Size" on a row swaps in the size step for that product,
   * and the store's quickViewFor decides which is showing. Closing always returns it to the list, so
   * it never reopens mid-task on a product the shopper has moved on from.
   *
   * ACCESSIBILITY. The panel is a modal dialog and behaves like one: role="dialog" + aria-modal,
   * named by its heading; focus is trapped inside it while it is open and returned to the star that
   * opened it on close; Escape closes it; and the page behind it is scroll-locked. The theme's own
   * trapFocus / removeTrapFocus are used rather than a second implementation, so there is one focus
   * trap on the page rather than two fighting.
   */
  class WishlistDrawer extends HTMLElement {
    /** Rendered width of the row thumbnail at the design viewports. */
    static THUMBNAIL_WIDTH = 135;
    /** Past this many saved products a dot apiece stops indicating anything — see renderPager. */
    static MAX_DOTS = 7;

    connectedCallback() {
      this.overlay = this.querySelector('[data-wishlist-overlay]');
      this.panel = this.querySelector('[data-wishlist-panel]');
      this.body = this.querySelector('[data-wishlist-body]');
      this.list = this.querySelector('[data-wishlist-list]');
      this.emptyState = this.querySelector('[data-wishlist-empty]');
      this.signup = this.querySelector('[data-wishlist-signup]');
      this.announce = this.querySelector('[data-wishlist-announce]');
      this.heading = this.querySelector('[data-wishlist-heading]');
      this.backButton = this.querySelector('[data-wishlist-back]');
      this.closeButton = this.querySelector('[data-wishlist-close]');
      this.quick = this.querySelector('[data-wishlist-quick]');

      this.rowTemplate = this.querySelector('[data-wishlist-row-template]');
      this.skeletonTemplate = this.querySelector('[data-wishlist-skeleton-template]');
      this.quickTemplate = this.querySelector('[data-wishlist-quick-template]');

      /*
       * The saved items joined to their live Shopify data. `entries` is what we hold; `resolvedKey`
       * is the list we last finished resolving. Both flags the render reads are DERIVED from that
       * rather than stored, because a stored flag has to be initialised to something and every value
       * is wrong in some state the drawer actually reaches. Deriving means the first render of any
       * state already carries the right answer, which matters: a shopper opening the drawer sees
       * whatever its first paint says.
       */
      this.entries = [];
      this.resolvedKey = null;
      this.rowsById = {};
      this.removedTitle = null;
      this.wasOpen = false;
      this.previousScreen = null;

      /*
       * What the CTA row is saying, and which product it is saying it about. Tagged rather than
       * reset, so a failure cannot follow the shopper onto the next product: it is a live region,
       * and a message that lands under the wrong title is worse than one that arrives a frame late.
       */
      this.cartStatus = { entryKey: null, kind: 'idle', message: '' };

      this.overlay.addEventListener('click', () => actions.setOpen(false));
      this.closeButton.addEventListener('click', () => actions.setOpen(false));
      this.backButton.addEventListener('click', () => actions.setQuickView(null));
      this.list.addEventListener('click', this.onListClick.bind(this));

      const viewCart = this.querySelector('[data-wishlist-view-cart]');
      if (viewCart) viewCart.addEventListener('click', this.onViewCart.bind(this));

      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-wishlist-row-title]')) report('product-opened', null);
      });

      this.onKeydown = (event) => {
        if (event.key === 'Escape' && state.isOpen) actions.setOpen(false);
      };
      document.addEventListener('keydown', this.onKeydown);

      this.unsubscribe = subscribe(this.onStateChange.bind(this));
      this.render();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      document.removeEventListener('keydown', this.onKeydown);
    }

    onStateChange() {
      if (state.isOpen !== this.wasOpen) {
        this.wasOpen = state.isOpen;
        if (state.isOpen) this.open();
        else this.close();
      }

      this.resolveEntries();
      this.render();
      this.moveFocusAcrossScreens();
    }

    /* Opening and closing -------------------------------------------------------------------- */

    open() {
      this.panel.classList.add('vlb-wl-drawer--open');
      this.overlay.classList.add('vlb-wl-overlay--open');
      document.documentElement.classList.add('vlb-wl-scroll-locked');

      // A fresh session in the panel: last session's "Removed X" must not be sitting in the
      // accessibility tree waiting to be read out as if it had just happened.
      this.removedTitle = null;

      if (typeof trapFocus === 'function') trapFocus(this.panel, this.closeButton);
    }

    close() {
      this.panel.classList.remove('vlb-wl-drawer--open');
      this.overlay.classList.remove('vlb-wl-overlay--open');
      document.documentElement.classList.remove('vlb-wl-scroll-locked');

      if (typeof removeTrapFocus === 'function') removeTrapFocus(lastOpener || undefined);
    }

    /* Resolving the saved list --------------------------------------------------------------- */

    listKey() {
      return state.items.map((item) => item.handle).join(',');
    }

    /**
     * A closed drawer resolves nothing, so saving from a product card costs one write and no
     * requests; already-resolved entries are KEPT rather than dropped, so reopening shows the list
     * immediately instead of flashing a loading state.
     */
    resolveEntries() {
      if (!state.isOpen) return;

      const key = this.listKey();
      if (key === this.resolvedKey) return;

      if (!state.items.length) {
        this.entries = [];
        this.resolvedKey = key;
        return;
      }

      loadEntries(state.items).then((loaded) => {
        // The list may have changed again while this was in flight; the newer resolve wins.
        if (this.listKey() !== key) return;
        this.entries = loaded;
        this.resolvedKey = key;
        this.render();
      });
    }

    /**
     * What we hold, narrowed to what is still saved. A product removed from the list is dropped here
     * on the very render the removal causes, so its row never outlives the tap that removed it.
     */
    visibleEntries() {
      return this.entries.filter((entry) =>
        state.items.some((item) => itemKey(item) === itemKey(entry.item))
      );
    }

    /* The list screen ------------------------------------------------------------------------ */

    /**
     * One saved product's row, built or brought up to date.
     *
     * Pricing follows the variant the shopper had chosen when they saved, when there was one and it
     * still exists; otherwise it falls back to the product's own. That matters on a catalogue where
     * sizes are priced differently: showing the product price next to a saved XXL that costs more
     * would quote a number the cart won't honour. Both numbers come off the same object for the same
     * reason — a variant's price beside the PRODUCT'S compare-at would invent a discount neither one
     * offers.
     */
    fillRow(element, entry) {
      const product = entry.product;
      const savedVariant = product.variants.find((variant) => variant.id === entry.item.variantId);
      const priced = savedVariant || product;

      const image = element.querySelector('.vlb-wl-row__image');
      const nextSrc = sized(product.featured_image, WishlistDrawer.THUMBNAIL_WIDTH * 2);
      if (image.getAttribute('src') !== nextSrc) image.setAttribute('src', nextSrc);
      image.alt = product.title;

      const title = element.querySelector('[data-wishlist-row-title]');
      title.textContent = product.title;
      title.href = productUrl(product.handle, savedVariant && savedVariant.id);

      renderPrice(element.querySelector('[data-wishlist-row-price]'), priced.price, priced.compare_at_price);

      const savedSize = element.querySelector('[data-wishlist-row-saved-size]');
      const savedSizeLabel = sizeLabelOf(product, savedVariant);
      savedSize.hidden = !savedSizeLabel;
      if (savedSizeLabel) {
        element.querySelector('[data-wishlist-row-saved-size-value]').textContent = savedSizeLabel;
      }

      const soldOut = isSoldOut(entry);
      const chooseSize = element.querySelector('[data-wishlist-choose-size]');
      const soldOutLabel = element.querySelector('[data-wishlist-row-sold-out]');

      /*
       * "Update Size" once a size is already saved, "Choose Size" while none is. The design draws
       * both, and the difference is the only thing on the row telling a shopper whether the picker
       * will open empty or on the size they already chose.
       *
       * `savedVariant` is the right test rather than the stored id on its own: a size that has since
       * been delisted leaves nothing to update, so that row asks for a fresh choice instead of
       * offering to change one that is gone.
       */
      chooseSize.textContent = savedVariant ? strings.rowUpdateSize : strings.rowChooseSize;
      chooseSize.hidden = soldOut;
      chooseSize.id = sizeButtonId(itemKey(entry.item));
      chooseSize.dataset.entryKey = itemKey(entry.item);
      soldOutLabel.hidden = !soldOut;

      const remove = element.querySelector('[data-wishlist-remove]');
      remove.id = removeButtonId(itemKey(entry.item));
      remove.dataset.entryKey = itemKey(entry.item);
      remove.setAttribute('aria-label', fill(strings.rowRemove, { product: product.title }));
    }

    /**
     * The list, reconciled by product id rather than rebuilt.
     *
     * Rebuilding would drop every row's already-decoded thumbnail and reload it, which is exactly
     * the flicker the eager loading exists to avoid — so a row that survives a re-render keeps its
     * element and only its fields are brought up to date.
     */
    renderList(visible) {
      const next = {};

      visible.forEach((entry, index) => {
        // Keyed by the ENTRY, so two sizes of one product keep two rows rather than fighting over one.
        const id = itemKey(entry.item);
        const element = this.rowsById[id] || this.rowTemplate.content.firstElementChild.cloneNode(true);

        this.fillRow(element, entry);
        next[id] = element;

        // Put it where it belongs without moving anything already in the right place.
        const atIndex = this.list.children[index];
        if (atIndex !== element) this.list.insertBefore(element, atIndex || null);
      });

      /*
       * Everything the list still holds that this render did not place.
       *
       * Walking the list's own children rather than the rows we track by id, because the two are
       * not the same set: the placeholder rows carry no product and were never in that map, so a
       * cleanup keyed on it left every one of them behind — the wishlist resolved, its real rows
       * were inserted in front, and the skeletons simply stayed underneath, sweeping forever. Rows
       * for products that have since gone are removed by the same pass.
       */
      const placed = new Set(Object.values(next));
      Array.from(this.list.children).forEach((child) => {
        if (!placed.has(child)) child.remove();
      });

      this.rowsById = next;
    }

    /** The placeholder rows, drawn one per SAVED ITEM so the list is its final length immediately. */
    renderSkeletons(count) {
      this.rowsById = {};
      this.list.textContent = '';
      for (let index = 0; index < count; index += 1) {
        this.list.appendChild(this.skeletonTemplate.content.firstElementChild.cloneNode(true));
      }
    }

    /** Puts a message into the list's live region, or clears it. */
    say(message, visible) {
      this.announce.textContent = '';
      if (!message) return;

      const paragraph = document.createElement('p');
      paragraph.className = visible ? 'vlb-wl-drawer__status' : 'vlb-wl-visually-hidden';
      paragraph.textContent = message;
      this.announce.appendChild(paragraph);
    }

    render() {
      const visible = this.visibleEntries();
      const hasResolved = this.resolvedKey === this.listKey();

      /*
       * There is nothing truthful to show yet: the list has items, this has not finished resolving
       * THIS list, and nothing already held survives to stand in for it. Once the drawer holds rows,
       * a slow refresh updates them in place rather than blanking them.
       */
      const isLoading = state.isOpen && state.items.length > 0 && !hasResolved && visible.length === 0;

      /*
       * A wishlist the storefront answered for and returned nothing on. Gated on hasResolved — the
       * question is whether the answer came back empty, not whether we happen to be holding nothing:
       * a list we have not asked about yet is also holding nothing, and telling that shopper their
       * saved products failed to load would be a lie about the one thing this panel is trusted for.
       */
      const failedToLoad = state.items.length > 0 && hasResolved && visible.length === 0;

      this.emptyState.hidden = state.items.length !== 0;

      if (isLoading) {
        this.renderSkeletons(state.items.length);
        // Said, not shown — the skeleton rows are the seeing shopper's half of this, and they are
        // aria-hidden precisely so the wait is announced once, in words, rather than as empty rows.
        this.say(strings.loading, false);
      } else {
        this.renderList(visible);
        if (failedToLoad) this.say(strings.failed, true);
        else if (this.removedTitle) this.say(fill(strings.removed, { product: this.removedTitle }), false);
        else this.say(null);
      }

      // The sign-up panel renders throughout a populated list rather than appearing once the rows
      // had: it sits on the floor of a short list, and appearing later would move it twice.
      this.signup.hidden = !(isLoading || visible.length > 0);

      this.renderQuickView(visible);
    }

    /*
     * Removing a row, without dropping the shopper who did it.
     *
     * A remove button destroys itself, and focus on a destroyed element falls to <body> — inside a
     * focus trap that means the next Tab starts from the top of the dialog, which for someone
     * clearing three items in a row is three journeys back down the list. So focus moves down the
     * list the way the eye does: the next row's remove button, the previous one when the last row
     * went, and the panel's close button when nothing is left to remove.
     *
     * The removal is also said out loud. Losing a row is the one action here with no visible
     * confirmation of WHAT went — the list simply gets shorter — and a screen reader announces
     * nothing at all for a node that quietly disappears.
     */
    onListClick(event) {
      const remove = event.target.closest('[data-wishlist-remove]');
      if (remove) {
        const key = remove.dataset.entryKey;
        const visible = this.visibleEntries();
        const index = visible.findIndex((entry) => itemKey(entry.item) === key);
        const neighbour = index === -1 ? null : visible[index + 1] || visible[index - 1];

        this.removedTitle = index === -1 ? null : visible[index].product.title;
        actions.remove(key);

        // The close button when the row we picked has itself gone. Landing on <body> inside a focus
        // trap is the one outcome worth avoiding — the next Tab would restart at the top.
        const target = neighbour && document.getElementById(removeButtonId(itemKey(neighbour.item)));
        (target || this.closeButton).focus();
        return;
      }

      const chooseSize = event.target.closest('[data-wishlist-choose-size]');
      if (chooseSize) actions.setQuickView(chooseSize.dataset.entryKey);
    }

    /*
     * "View Cart" opens the storefront's own cart drawer rather than navigating, because that is
     * what every other route to the cart on this site does. It stays a real link underneath, so a
     * modifier-click still opens a new tab and a page with no cart drawer still navigates: those
     * clicks are the shopper asking the BROWSER for something, and a preventDefault would quietly
     * take it away.
     */
    onViewCart(event) {
      const plain = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
      if (!plain || !openCartDrawer()) return;

      event.preventDefault();
      report('cart-opened', null);
      // Only once the cart is actually opening: the theme's drawer sits below the wishlist in the
      // stacking order, so leaving ours up would hide the thing we just opened.
      actions.setOpen(false);
    }

    /* The size step -------------------------------------------------------------------------- */

    /**
     * The size on screen for this product — always one of the sizes it is offering, or nothing.
     *
     * Read in this order: what the shopper picked in this drawer session, then the size they had
     * chosen when they saved the product, then the single purchasable size if that is all there is.
     * Each candidate has to still be available to survive, so a size that sells out while the drawer
     * is open stops being offered instead of sitting there ready to add.
     *
     * A product with exactly one purchasable size — the catalogue's ONE SIZE accessories — is not a
     * choice, and asking for it is a dead end. Selecting it for them decides nothing on their
     * behalf, which is why nothing else is ever preselected: quietly defaulting to a size would let
     * someone buy one they never picked.
     *
     * Derived, not held. Everything downstream reads it, so the price, the picker and the button
     * cannot disagree about which size is chosen.
     */
    chosenVariant(entry, choices) {
      const match = [
        choices.find((choice) => choice.variantId === state.sizeChoices[itemKey(entry.item)]),
        choices.find((choice) => choice.variantId === entry.item.variantId),
        choices.length === 1 ? choices[0] : undefined,
      ].find((candidate) => candidate && candidate.available);

      return match ? match.variantId : null;
    }

    renderSizeSelect(select, choices, chosen, soldOut, entry) {
      /*
       * The placeholder's value is a sentinel and deliberately not "". A <select> also reports ""
       * when NOTHING is selected, which is the state the browser leaves it in whenever the value it
       * is handed matches no option — so with "" the assignment that would show the placeholder is
       * the one that gets skipped exactly when it is needed, and the field renders blank with no
       * size, no placeholder, and nothing that will ever put one there.
       */
      select.textContent = '';

      const placeholder = document.createElement('option');
      placeholder.value = 'none';
      placeholder.disabled = true;
      placeholder.textContent = soldOut ? strings.sizeSoldOut : strings.sizePlaceholder;
      select.appendChild(placeholder);

      /*
       * Sizes with no purchasable variant are rendered disabled rather than hidden, so the shopper
       * can see that the size exists and is gone rather than wondering whether the product is made
       * in it. That holds when EVERY size has gone too: the field stays a field, because disabling
       * it outright empties the one place that could still answer "what did this come in?".
       */
      /*
       * A size saved on ANOTHER row of this same product is not offered again: the shopper already
       * has it, and choosing it here would make a second row identical to one they can see. It is
       * disabled and says why, because a size disabled in silence reads as sold out — which is the
       * one thing the plain disabled state already means here.
       *
       * Sold out wins when a size is both. Not being able to buy it at all is the more useful
       * thing to know, and it is the reason that outlasts anything the shopper does to their list.
       *
       * Read from the store on every render rather than worked out when the picker opened, so a
       * size saved or removed while it is on screen is offered or withdrawn straight away.
       */
      const thisEntry = itemKey(entry.item);
      choices.forEach((choice) => {
        const option = document.createElement('option');
        const choiceKey = keyOf(entry.item.id, choice.variantId);
        const savedElsewhere = choiceKey !== thisEntry && isSavedKey(choiceKey);

        option.value = String(choice.variantId);
        option.disabled = !choice.available || savedElsewhere;
        option.textContent =
          choice.available && savedElsewhere
            ? fill(strings.sizeAlreadySaved, { size: choice.label })
            : choice.label;
        select.appendChild(option);
      });

      select.value = chosen === null ? 'none' : String(chosen);
    }

    renderPager(pager, visible, index) {
      // A pager with one dot and two arrows that go nowhere is noise.
      pager.hidden = visible.length < 2;
      if (visible.length < 2) return;

      const dots = pager.querySelector('[data-wishlist-dots]');
      const count = pager.querySelector('[data-wishlist-pager-count]');

      /*
       * One dot per product as long as the row holds one line, and past that a count. The design was
       * drawn against a handful of saved items and is exactly right there; a wishlist of twenty is a
       * different object. Twenty dots do not fit, and wrapping them is worse than not fitting: the
       * row becomes a block of identical circles nobody counts, and it shoves the arrows onto lines
       * of their own, where two controls labelled "previous" and "next" now read as up and down.
       * "4 of 21" is the thing a dot row was standing in for anyway.
       */
      const useCount = visible.length > WishlistDrawer.MAX_DOTS;
      dots.hidden = useCount;
      count.hidden = !useCount;

      if (useCount) {
        count.textContent = fill(strings.pagerCount, { index: index + 1, count: visible.length });
      } else {
        dots.textContent = '';
        visible.forEach((_, position) => {
          const dot = document.createElement('li');
          dot.className = `vlb-wl-pager__dot${position === index ? ' vlb-wl-pager__dot--active' : ''}`;
          dots.appendChild(dot);
        });
      }

      // It wraps, so a shopper sizing the last item lands back on the first rather than hitting a
      // dead control — which is what the design's two full-opacity arrows on the first item mean.
      const step = (delta) => {
        const next = visible[(index + delta + visible.length) % visible.length];
        if (next) actions.setQuickView(itemKey(next.item));
      };
      pager.querySelector('[data-wishlist-pager-prev]').onclick = () => step(-1);
      pager.querySelector('[data-wishlist-pager-next]').onclick = () => step(1);
    }

    renderQuickView(visible) {
      const showing = state.quickViewFor !== null;

      this.body.hidden = showing;
      this.quick.hidden = !showing;
      this.backButton.hidden = !showing;
      this.heading.classList.toggle('vlb-wl-visually-hidden', showing);

      if (!showing) {
        this.quick.textContent = '';
        return;
      }

      const index = visible.findIndex((entry) => itemKey(entry.item) === state.quickViewFor);
      const entry = index === -1 ? null : visible[index];

      // A product removed from the list while it was being sized: the store drops quickViewFor in
      // the same step, so this only renders for the moment in between.
      if (!entry) {
        this.quick.textContent = '';
        return;
      }

      if (!this.quick.firstElementChild) this.quick.appendChild(this.quickTemplate.content.cloneNode(true));

      const product = entry.product;
      const gallery = this.quick.querySelector('wishlist-gallery');
      const price = this.quick.querySelector('[data-wishlist-quick-price]');
      const select = this.quick.querySelector('[data-wishlist-size]');
      const status = this.quick.querySelector('[data-wishlist-quick-status]');
      const cta = this.quick.querySelector('[data-wishlist-add-to-bag]');

      this.quick.querySelector('[data-wishlist-quick-title]').textContent = product.title;
      if (gallery && typeof gallery.show === 'function') gallery.show(product);

      const choices = getSizeChoices(product, entry.item.variantId);
      const chosen = this.chosenVariant(entry, choices);
      const soldOut = isSoldOut(entry);

      // Until a size is chosen there is no variant to price, so the product's own numbers stand in —
      // and once there is one, both numbers move together so a discount is never half-applied.
      const priced = product.variants.find((variant) => variant.id === chosen) || product;

      renderPrice(price, priced.price, priced.compare_at_price);
      this.renderSizeSelect(select, choices, chosen, soldOut, entry);

      select.onchange = () => {
        if (select.value === 'none') return;
        this.cartStatus = { entryKey: state.quickViewFor, kind: 'idle', message: '' };
        actions.setSizeChoice(state.quickViewFor, Number(select.value));
        report('size-selected', entry.item);
      };

      const said =
        this.cartStatus.entryKey === state.quickViewFor ? this.cartStatus : { kind: 'idle', message: '' };
      const isAdding = said.kind === 'adding';

      status.textContent = '';
      if (said.kind === 'error') {
        const error = document.createElement('p');
        error.className = 'vlb-wl-quick__error';
        error.textContent = said.message;
        status.appendChild(error);
      }

      cta.textContent = soldOut ? strings.quickSoldOut : isAdding ? strings.adding : strings.addToBag;
      cta.disabled = soldOut || chosen === null || isAdding;
      cta.onclick = () => this.addChosenToBag(entry, chosen, isAdding);

      this.renderPager(this.quick.querySelector('[data-wishlist-pager]'), visible, index);
    }

    addChosenToBag(entry, chosen, isAdding) {
      if (chosen === null || isAdding) return;

      this.cartStatus = { entryKey: itemKey(entry.item), kind: 'adding', message: '' };
      this.render();

      addToBag(chosen).then((result) => {
        if (!result.ok) {
          this.cartStatus = { entryKey: itemKey(entry.item), kind: 'error', message: result.reason };
          this.render();
          return;
        }

        this.cartStatus = { entryKey: null, kind: 'idle', message: '' };

        /*
         * The item disappears from the wishlist — Diana, answering the QA checklist. Only ever after
         * a successful add: a cart that refused the product leaves the wishlist holding it. `bag`
         * and not `remove`, so the removal the code decided on is not reported as the shopper
         * deciding they no longer want the product.
         */
        actions.bag(itemKey(entry.item));

        /*
         * The theme's cart drawer opens itself at the end of renderContents, and it sits far below
         * the wishlist in the stacking order — so handing off means CLOSING, not stepping back to
         * the list, or the shopper is left looking at their wishlist over an invisible cart. Where
         * there was no cart drawer, the drawer simply returns to the list, one row shorter, with the
         * banner above it saying why.
         */
        if (result.handedOffToCart) actions.setOpen(false);
      });
    }

    /*
     * Keyboard focus across the drawer's two screens.
     *
     * Each swap removes the control that caused it — "Choose Size" on the way in, "Back to List" on
     * the way out — and focus on a removed element falls to <body>. The trap keeps that from
     * escaping the dialog, but the shopper's place in it is gone and a screen reader is told nothing
     * about where they now are. So focus follows the task instead: into the size step's back
     * control, and back out onto the very row that opened it.
     */
    moveFocusAcrossScreens() {
      const previous = this.previousScreen;
      this.previousScreen = state.quickViewFor;

      const entered = previous === null && state.quickViewFor !== null;
      const left = previous !== null && state.quickViewFor === null;
      if (!state.isOpen || (!entered && !left)) return;

      if (entered) {
        this.backButton.focus();
        return;
      }

      /*
       * Coming back out, the button that opened the step is the place to land — but it is not always
       * still there. A row removed while it was being sized has taken it with it, and a row that
       * sold out between opening the drawer and paging onto it never had one, since the list shows
       * those the words instead of the control. So focus walks down what that row still has.
       */
      const target =
        document.getElementById(sizeButtonId(previous)) ||
        document.getElementById(removeButtonId(previous)) ||
        this.closeButton;

      target.focus();
    }
  }

  /* <wishlist-toast> ------------------------------------------------------------------------- */

  /**
   * The confirmation banner, in both the things it has to confirm.
   *
   * A SAVE made outside the drawer, because the stars live on product cards, in the nav and on the
   * product page, where the only other feedback is a small outline filling in — easy to miss
   * mid-scroll. And a product LEAVING FOR THE BAG, where the reasoning is stronger still: that one
   * takes the row away with it, and on a page with the theme's cart drawer the wishlist has already
   * closed behind it, so without this the disappearance happens somewhere the shopper cannot see.
   *
   * The element itself is the live region and stays empty between showings — a role="status" that
   * arrives together with its text is a region the screen reader was never watching, and is
   * routinely announced late or not at all.
   *
   * The design gives it no dismiss control, so it has none: adding a button would put a focusable
   * element in the tab order that vanishes from under the shopper seconds later, which is worse for
   * keyboard users than a banner they can already ignore. What makes that safe is that it never
   * takes a click — pointer-events: none in the stylesheet — so it cannot cover anything it sits
   * over.
   */
  class WishlistToast extends HTMLElement {
    /**
     * How long after its time is up the element is torn down anyway.
     *
     * The banner is normally removed by its own exit animation ending, not by a clock; this is only
     * the answer to "what if that never arrives" — animations turned off at the OS level, a tab
     * backgrounded across the whole showing — so it is generous rather than tight. Nothing is paid
     * for the generosity: the animation's `forwards` fill leaves the banner at opacity 0, and it
     * never took a pointer event, so an extra second mounted is an extra second of something nobody
     * can see or touch.
     */
    static BACKSTOP = 1000;

    connectedCallback() {
      this.template = this.querySelector('[data-wishlist-toast-template]');
      this.showing = null;
      this.unsubscribe = subscribe(this.render.bind(this));
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      clearTimeout(this.timer);
    }

    render() {
      /*
       * Which confirmation this is, and not just "is one up" — adding a product to the bag from the
       * drawer within the banner's own lifetime is the same handle twice running, and on the handle
       * alone nothing would re-render: the banner would change its words without re-measuring where
       * it sits or restarting its countdown, so "Moved to Your Bag!" would inherit whatever was left
       * of the save's.
       */
      const showing = state.toast ? `${state.toast.kind}:${state.toast.handle}` : null;
      if (showing === this.showing) return;
      this.showing = showing;

      clearTimeout(this.timer);

      // A new element rather than the old one's text swapped out: a CSS animation does not restart
      // when the node it is on merely changes, so a second banner would appear without its entrance
      // and inherit the first one's spent countdown, fading out early.
      const existing = this.querySelector('.vlb-wl-toast');
      if (existing) existing.remove();
      if (!state.toast) return;

      const element = this.template.content.firstElementChild.cloneNode(true);
      element.querySelector('.vlb-wl-toast__text').textContent =
        state.toast.kind === 'bagged' ? strings.toastBagged : strings.toastSaved;

      /*
       * MEASURED ONCE, as the banner mounts, and then left alone.
       *
       * It used to be re-measured continuously, to keep the banner pinned under a header that moves.
       * On this storefront the header does not move — header-group.json sets sticky_header_type
       * "always" and the theme's own StickyHeader returns early for that case, so there is no header
       * animation to follow. What does still move is the group's LOWEST edge, because two of its
       * sections sit below the header and scroll away normally, and following that is what QA was
       * complaining about rather than benefiting from: the banner slid around under a moving anchor
       * while it was trying to play its entrance, and jumped when the last section cleared the top.
       *
       * Measuring once costs nothing in the common case, because there the two answers are
       * identical: by the time anyone has scrolled far enough to be looking at a product card,
       * everything above and below the header has gone and the group's bottom edge IS the stuck
       * header's. They differ only within the first screenful, and there a banner that stays put
       * beats one that slides.
       *
       * Written on the banner's own element rather than on <html>: custom properties inherit, so a
       * root write would invalidate the computed style of every element on the page to deliver a
       * number that two nodes read.
       */
      element.style.setProperty('--vlb-wl-toast-top', `${Math.round(headerGroupBottom())}px`);
      element.style.setProperty('--vlb-wl-toast-life', `${config.toastDuration}ms`);

      /*
       * Horizontally, the banner follows the cart drawer whenever one is open. The design draws it
       * over the drawer and centred on it, which is also where the shopper is looking: they pressed
       * a star inside the drawer, and a banner centred on the page behind it answers somewhere they
       * are not. Read at raise time rather than cached, because the drawer's width is the theme's
       * and changes with the viewport.
       *
       * Left unset when no drawer is open, so every other placement keeps the page's own centre
       * through the stylesheet's fallback rather than through a number written here.
       */
      const openDrawer = document.querySelector('cart-drawer.active');
      const box = openDrawer && openDrawer.querySelector('.drawer__inner');
      if (box) {
        const rect = box.getBoundingClientRect();

        /*
         * Only when the panel is actually on screen. The drawer is moved by a transform and its
         * `active` class goes on before that transform has finished — and stays on for the beat
         * after a re-render while it settles — so the class alone will hand back the panel's parked
         * position, a full viewport to the right. Centring on that would put the banner off the
         * side of the screen, which is worse than the page-centred default it replaced.
         */
        const onScreen = rect.width > 0 && rect.left < window.innerWidth && rect.right > 0;
        if (onScreen) {
          element.style.setProperty('--vlb-wl-toast-left', `${Math.round(rect.left + rect.width / 2)}px`);
        }
      }

      /*
       * What actually takes the banner away: the exit animation saying it has finished. The
       * stylesheet owns both when the fade starts and how long it takes, so it is the only thing
       * that knows when there is nothing left to look at — a duplicate of that sum here is a second
       * clock to keep in step, and it would already be out of step by however long the browser took
       * to start the animation. The name is checked because the entrance ends on this element too.
       */
      element.addEventListener('animationend', (event) => {
        if (event.animationName === 'vlb-wl-toast-out') actions.hideToast();
      });

      this.timer = setTimeout(actions.hideToast, config.toastDuration + WishlistToast.BACKSTOP);
      this.appendChild(element);
    }
  }

  /* Registration ----------------------------------------------------------------------------- */

  if (!customElements.get('wishlist-star')) customElements.define('wishlist-star', WishlistStar);
  if (!customElements.get('wishlist-gallery')) customElements.define('wishlist-gallery', WishlistGallery);
  if (!customElements.get('wishlist-drawer')) customElements.define('wishlist-drawer', WishlistDrawer);
  if (!customElements.get('wishlist-toast')) customElements.define('wishlist-toast', WishlistToast);

  /*
   * The header's star is what marks the body for the gutter override in the stylesheet — the theme's
   * header is a four-column grid with nothing spare at laptop widths, and the star is funded from
   * the margins rather than from the menus. Set here rather than in Liquid so it is only ever true
   * when a star was actually rendered into the header.
   */
  if (document.querySelector('wishlist-star[context="nav"]')) {
    document.body.classList.add('vlb-wl-nav-star');
  }
})();
